import { z } from "zod";
import { AccessFieldsSchema } from "./access/types";

// ============================================
// Enums
// ============================================

/** @deprecated Used only by concept.current.state (derived by controller). Prefer MessageStateEnum for new code. */
export const ConceptStateEnum = z.enum([
  "PENDING",
  "FINISHED",
  "ERROR",
  "ARCHIVED",
]);
export type ConceptState = z.infer<typeof ConceptStateEnum>;

export const MessageStateEnum = z.enum([
  "PENDING",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
  "REDACTED",
]);
export type MessageState = z.infer<typeof MessageStateEnum>;

export const RegionServedEnum = z.enum([
  "USCA",
  "LATAM",
  "WEUR",
  "CSEE",
  "MENA",
  "SSA",
  "SASIA",
  "SEA",
  "NEA_GCN",
  "OCE",
  "CASR",
]);
export type RegionServed = z.infer<typeof RegionServedEnum>;

export const MessageRoleEnum = z.enum(["user", "assistant", "system", "tool"]);
export type MessageRole = z.infer<typeof MessageRoleEnum>;

export const ActionTypeEnum = z.enum([
  "LLM_GENERATION",
  "ARCHIVE",
  "USER_EDIT",
  "MATERIAL_REPLACEMENT",
  "SEGMENT_CACHE",
  "METADATA_EDIT",
  "UPLOAD",
  "BOARD_GENERATE",
]);
export type ActionType = z.infer<typeof ActionTypeEnum>;

// ============================================
// Palette & Color Schemas
// ============================================

// Palette color in LAB color space
export const PaletteColorSchema = z.object({
  l: z.number().min(0).max(100),
  a: z.number().min(-128).max(127),
  b: z.number().min(-128).max(127),
});
export type PaletteColor = z.infer<typeof PaletteColorSchema>;

// Palette entry with text description and LAB color
export const PaletteEntrySchema = z.object({
  text: z.string().optional(),
  lab: PaletteColorSchema.optional(),
});
export type PaletteEntry = z.infer<typeof PaletteEntrySchema>;

// Concept palette with dominant, secondary, accent colors
export const ConceptPaletteSchema = z.object({
  dominant: PaletteEntrySchema.optional(),
  secondary: PaletteEntrySchema.optional(),
  accent: PaletteEntrySchema.optional(),
});
export type ConceptPalette = z.infer<typeof ConceptPaletteSchema>;

// ============================================
// Content Block Schemas (OpenAI Responses API pattern)
// ============================================

export const InputTextContentSchema = z.object({
  type: z.literal("input_text"),
  text: z.string(),
});
export type InputTextContent = z.infer<typeof InputTextContentSchema>;

export const OutputTextContentSchema = z.object({
  type: z.literal("output_text"),
  text: z.string(),
  annotations: z
    .array(
      z
        .object({
          type: z.string(),
        })
        .passthrough(),
    )
    .optional(),
});
export type OutputTextContent = z.infer<typeof OutputTextContentSchema>;

export const InputImageContentSchema = z.object({
  type: z.literal("input_image"),
  image_url: z
    .string()
    .url()
    .max(2048)
    .refine((url) => !url.startsWith("data:"), {
      message: "Data URIs are not allowed. Use a hosted URL instead.",
    })
    .optional(),
  file_id: z.string().optional(),
  // Source/use tag for the image. Written by the edit box (frontend) with the
  // image's real category (e.g. 'Material'/'Object'/'Other' from the drop dialog,
  // or a REFERENCE_IMAGE_TAG if the image came from the brief intake). Falls back
  // to 'reference' when no category is known. Free-form string for forward-compat.
  use: z.string().optional(),
});
export type InputImageContent = z.infer<typeof InputImageContentSchema>;

export const InputPaletteContentSchema = z.object({
  type: z.literal("input_palette"),
  colors: z.array(PaletteColorSchema).max(20),
});
export type InputPaletteContent = z.infer<typeof InputPaletteContentSchema>;

export const InputMaterialContentSchema = z.object({
  type: z.literal("input_material"),
  material_id: z.string(),
  image_url: z.string().url().max(2048).optional(),
  name: z.string().optional(),
  // The following optional fields are written by the edit box (frontend) when a
  // material is attached, and by the backend material-replacement controller on
  // the COMPLETED message. They describe the attached material's composition.
  is_paint: z.boolean().optional(),
  paint_finish: z.string().optional(),
  custom_repeat: z.number().optional(),
  is_fixture: z.boolean().optional(),
});
export type InputMaterialContent = z.infer<typeof InputMaterialContentSchema>;

export const ContentBlockSchema = z.discriminatedUnion("type", [
  InputTextContentSchema,
  OutputTextContentSchema,
  InputImageContentSchema,
  InputPaletteContentSchema,
  InputMaterialContentSchema,
]);
export type ContentBlock = z.infer<typeof ContentBlockSchema>;

export const MessageContentSchema = z.array(ContentBlockSchema);
export type MessageContent = z.infer<typeof MessageContentSchema>;

// ============================================
// Edit Meta (denormalized analytics summary on edit-box user messages)
// ============================================

/**
 * Compact, queryable summary of an edit's composition, stamped as a top-level
 * field on the user message created from the concept "edit box". This is the
 * PRIMARY analytics surface — one object, no per-block walking required.
 *
 * Deploy-forward only: messages created before this feature shipped have no
 * `edit_meta`. All fields are additive/optional and backward-compatible.
 */
export const EditMetaSchema = z
  .object({
    // Region/targeting tool used for the edit.
    //  - 'segment'      : a surface segment was selected (carries segment_label)
    //  - 'paint'        : freehand paint mask drawn (editMode 'paint')
    //  - 'collage'      : object/image placed on canvas (editMode 'collage')
    //  - 'combined'     : both paint + placed objects (editMode 'combined')
    //  - 'whole_image'  : plain text edit / chat, no region targeting
    tool: z.enum(["segment", "paint", "collage", "combined", "whole_image"]),
    // Label of the selected segment (e.g. 'Walls'), when tool === 'segment'.
    segment_label: z.string().nullable().optional(),
    // Material attachment summary.
    has_material: z.boolean(),
    material_id: z.string().nullable().optional(),
    material_kind: z
      .enum(["paint", "fixture", "material"])
      .nullable()
      .optional(),
    custom_repeat: z.number().nullable().optional(),
    paint_finish: z.string().nullable().optional(),
    // Attached images: count + the real source/use tag of each (never invented).
    image_count: z.number(),
    image_uses: z.array(z.string()),
    // Free-text length of the instruction.
    text_len: z.number(),
  })
  .passthrough();
export type EditMeta = z.infer<typeof EditMetaSchema>;

// ============================================
// Threads Schema
// ============================================

export const ThreadSchema = z.object({
  id: z.string(), // Firestore auto-generated ID (not UUID)
  user_id: z.string(),
  openai_thread_id: z.string().nullable().optional(),
  langgraph_thread_id: z.string().nullable().optional(),
  created_at: z.unknown(), // Firestore Timestamp
  metadata: z.record(z.unknown()).nullable().optional(),
});
export type Thread = z.infer<typeof ThreadSchema>;

export const ThreadInputSchema = ThreadSchema.omit({
  id: true,
  created_at: true,
});
export type ThreadInput = z.infer<typeof ThreadInputSchema>;

// ============================================
// Messages Schema
// ============================================

export const MessageSchema = z.object({
  id: z.string(), // Firestore auto-generated ID (not UUID)
  thread_id: z.string(),
  concept_id: z.string().optional(), // Set server-side by addMessageController
  role: MessageRoleEnum,
  content: MessageContentSchema,
  state: MessageStateEnum.optional(), // Optional on READ — pre-migration data lacks state
  error_code: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  tool_call_id: z.string().nullable().optional(),
  structured_output: z.record(z.unknown()).nullable().optional(),
  edit_meta: EditMetaSchema.nullable().optional(), // Analytics summary on edit-box user messages (deploy-forward)
  created_at: z.unknown(), // Firestore Timestamp
});
export type Message = z.infer<typeof MessageSchema>;

export const MessageInputSchema = MessageSchema.omit({
  id: true,
  created_at: true,
});
export type MessageInput = z.infer<typeof MessageInputSchema>;

// ============================================
// Concept Versions Schema
// ============================================

export const ConceptVersionSchema = z.object({
  id: z.string(), // Firestore auto-generated ID (not UUID)
  concept_id: z.string(),
  name: z.string().nullable().optional(),
  img_url: z.string().nullable().optional(),
  segments_url: z.string().nullable().optional(),
  narrative: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  budget: z.string().nullable().optional(),
  space: z.string().nullable().optional(),
  tag: z.string().nullable().optional(),
  style: z.string().nullable().optional(),
  palette: ConceptPaletteSchema.nullable().optional(),
  collection_id: z.string().nullable().optional(),
  // Project (projectsScheme) this concept belongs to (#3544 Phase 4).
  // Part of the immutable metadata-snapshot set: PRESERVED_VERSION_FIELDS
  // derives from ConceptVersionSchema, and client copyVersionMetadata copies it.
  project_id: z.string().nullable().optional(),
  region: z
    .union([z.array(RegionServedEnum), z.literal("GLOBAL")])
    .nullable()
    .optional(),
  action_type: ActionTypeEnum,
  source_message_id: z.string().nullable().optional(),
  parent_version_id: z.string().nullable().optional(),
  credits_cost: z.number().optional(), // Set by backend when credits are deducted
  archived_at: z.unknown().nullable().optional(),
  created_at: z.unknown(), // Firestore Timestamp
});
export type ConceptVersion = z.infer<typeof ConceptVersionSchema>;

export const ConceptVersionInputSchema = ConceptVersionSchema.omit({
  id: true,
  created_at: true,
});
export type ConceptVersionInput = z.infer<typeof ConceptVersionInputSchema>;

// ============================================
// Concepts Schema (Firestore collection structure)
// ============================================

// Embedded current version - full copy of the active version's data
export const CurrentVersionSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  img_url: z.string().nullable().optional(),
  segments_url: z.string().nullable().optional(),
  narrative: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  budget: z.string().nullable().optional(),
  space: z.string().nullable().optional(),
  tag: z.string().nullable().optional(),
  style: z.string().nullable().optional(),
  palette: ConceptPaletteSchema.nullable().optional(),
  collection_id: z.string().nullable().optional(),
  // Project (projectsScheme) this concept belongs to (#3544 Phase 4).
  // Part of the immutable metadata-snapshot set: PRESERVED_VERSION_FIELDS
  // derives from ConceptVersionSchema, and client copyVersionMetadata copies it.
  project_id: z.string().nullable().optional(),
  region: z
    .union([z.array(RegionServedEnum), z.literal("GLOBAL")])
    .nullable()
    .optional(),
  state: ConceptStateEnum.optional(), // Legacy — replaced by concept.message_state + concept.is_busy
  action_type: ActionTypeEnum,
  created_at: z.unknown(), // Firestore Timestamp
});
export type CurrentVersion = z.infer<typeof CurrentVersionSchema>;

// Cumulative product association on a concept — append-only via arrayUnion.
// A product that ever touched the concept stays in the array even if later replaced.
export const ConceptProductEntrySchema = z.object({
  productId: z.string(),
  supplierId: z.string(),
  productType: z.string(),
  source: z.enum(["MATCH", "REPLACEMENT", "DRAG", "CHAT_MENTION"]),
});
export type ConceptProductEntry = z.infer<typeof ConceptProductEntrySchema>;

// ============================================
// Undo / Redo (durable head-move state)
// ============================================
//
// Undo and redo are HEAD MOVES over the append-only version log — no version is
// ever mutated or deleted. The authoritative audit trail is the append-only
// subcollection concepts/{id}/head_events; `undo_redo` below is a transactional
// projection stamped on the concept doc for cheap, reload-safe UI reads.
//
// The two stacks encode the head's actual trajectory (NOT raw created_at order,
// which is wrong after a supersede):
//   - undo_stack: predecessors of head; TOP (last element) = the undo target.
//   - redo_stack: undone forward versions; TOP (last element) = the redo target.
// Fold applied on each head move (see undoRedoService on the backend):
//   edit(from,to): if from != null push from onto undo_stack; redo_stack = []  (supersede)
//   undo(from,to): push from onto redo_stack; pop undo_stack
//   redo(from,to): push from onto undo_stack; pop redo_stack
export const HeadEventTypeEnum = z.enum(["edit", "undo", "redo"]);
export type HeadEventType = z.infer<typeof HeadEventTypeEnum>;

export const UndoRedoStateSchema = z.object({
  undo_stack: z.array(z.string()),
  redo_stack: z.array(z.string()),
  can_undo: z.boolean(),
  can_redo: z.boolean(),
});
export type UndoRedoState = z.infer<typeof UndoRedoStateSchema>;

// Append-only audit doc in concepts/{id}/head_events. Never mutated.
export const HeadEventSchema = z.object({
  id: z.string(),
  concept_id: z.string(),
  type: HeadEventTypeEnum,
  from_version_id: z.string().nullable(),
  to_version_id: z.string(),
  actor: z.string(),
  created_at: z.unknown(), // Firestore Timestamp
});
export type HeadEvent = z.infer<typeof HeadEventSchema>;

// ---- SketchUp Renders (render-from-3D-model) ---------------------------------

/** Where a concept was created from, when it isn't the web app. Stamped once,
 *  at concept creation; `source` stays "UPLOAD" so the concept still lists in
 *  the web Image Edit page. */
export const ConceptOriginEnum = z.enum(["sketchup"]);
export type ConceptOrigin = z.infer<typeof ConceptOriginEnum>;

/** Max length of a model ID (SketchUp extension: a UUID v4, 36 chars). */
export const SOURCE_MODEL_ID_MAX = 64;
export const SourceModelIdSchema = z.string().min(1).max(SOURCE_MODEL_ID_MAX);

/** Aspect ratios the Image Edit render accepts (`sketchRender.aspectRatio`).
 *  SYNC: must equal RENDER_ASPECT_RATIOS in ExpressApp
 *  src/ai/nodes/shared/renderSketch.ts (a test asserts it). */
export const SKETCH_RENDER_ASPECT_RATIOS = [
  "1:1",
  "2:3",
  "3:2",
  "3:4",
  "4:3",
  "4:5",
  "5:4",
  "9:16",
  "16:9",
  "21:9",
] as const;
export type SketchRenderAspectRatio =
  (typeof SKETCH_RENDER_ASPECT_RATIOS)[number];

const Vec3Schema = z.tuple([
  z.number().finite(),
  z.number().finite(),
  z.number().finite(),
]);

/** Where a render's input came from (SketchUp panel). Stored on the render's
 *  message as structured_output.sketch_render.source. Unknown keys are
 *  stripped: the SketchUp file name/path must never reach the server
 *  (concepts and messages are readable by anyone holding the id). */
export const SketchRenderSourceSchema = z.object({
  app: z.literal("sketchup"),
  modelId: SourceModelIdSchema,
  sceneName: z.string().max(200).optional(),
  camera: z
    .object({
      /** Inches, as SketchUp reports them. */
      eye: Vec3Schema,
      target: Vec3Schema,
      up: Vec3Schema,
      /** Degrees. */
      fov: z.number().finite(),
      perspective: z.boolean(),
      /** width / height */
      aspectRatio: z.number().finite().positive(),
    })
    .optional(),
  extensionVersion: z.string().min(1).max(32),
});
export type SketchRenderSource = z.infer<typeof SketchRenderSourceSchema>;

/** Mattoboard product IDs present in the model, sent with a render so they
 *  show as "Used Products" on the result. */
export const SketchRenderProductIdsSchema = z
  .array(z.string().min(1).max(128))
  .max(200);

export const ConceptSchema = z.object({
  id: z.string(),
  owner_id: z.string(),
  thread_id: z.string(),
  current_version_id: z.string(),
  current: CurrentVersionSchema,
  // Durable undo/redo projection (see UndoRedoStateSchema). Absent on
  // pre-feature concepts — the backend lazily initializes it on the first head
  // move, and the frontend falls back to linear created_at order until then.
  undo_redo: UndoRedoStateSchema.optional(),
  // Workspace the concept was created in. null/absent = Personal; string =
  // design firm id (team workspace). Stamped at creation by
  // addMessageInternal. Used by frontend hooks to filter pending-credit
  // optimistic deductions per workspace.
  // (Phase A.2a.1 / A.3.)
  design_firm_id: z.string().nullable().optional(),
  // Teams sharing: created_by, access, readers, editors (see ./access).
  ...AccessFieldsSchema.shape,
  message_state: MessageStateEnum.optional(), // Denormalized from latest message.state
  is_busy: z.boolean().optional(), // True when PENDING/PROCESSING
  source: z.string().optional(), // "UPLOAD" when created via upload flow
  // Set once at creation for concepts made outside the web app (the SketchUp
  // panel's Renders tab); drives the SketchUp badge. Absent otherwise.
  origin: ConceptOriginEnum.optional(),
  // The SketchUp model's Mattoboard ID (mattoboard/model_id attribute), set
  // once at creation. Queried by the panel's "This model" rail:
  // owner_id/design_firm_id + source_model_id, updated_at desc.
  source_model_id: SourceModelIdSchema.optional(),
  variantId: z.string().optional(), // source variant (set on finalizeConcept)
  remixId: z.string().optional(), // source variants-flow concept (set on finalizeConcept)
  // True when this concept was created by a non-charging generation (the free
  // first finalize per remix flow, issue #3381). Lets the frontend soft-deduct
  // skip it so an in-flight free generation doesn't show a phantom -1 credit.
  free_generation: z.boolean().optional(),
  // True while the "Fine Tune" flow is running its background product-finder +
  // metadata enrichment on a freshly committed concept. The editor uses it to
  // disable "Show Concept" and show a loading state in the matched-products
  // area — WITHOUT blocking edits to the committed image. Cleared by the backend
  // when enrichment finishes (success or failure).
  enrichment_pending: z.boolean().optional(),
  // Display-only card thumbnail (issue #3363): the first completed draft
  // variant's image, stamped by the backend's setConceptThumbnailIfMissing.
  // Deliberately NOT current.img_url — that field means "committed render"
  // and drives routing (/home/remix redirect) and graph selection.
  thumbnail_url: z.string().url().nullable().optional(),
  // Legacy: Google Shopping cache from removed POST /api/product-search/by-concept.
  // No longer written; optional for old Firestore documents.
  external_product_search: z.unknown().optional(),
  // Cumulative set of products that touched this concept (matched, applied, mentioned).
  // Written via arrayUnion — entries are immutable once added. Used for supplier-side
  // sales analytics ("which concepts featured my products").
  products: z.array(ConceptProductEntrySchema).optional(),
  // Denormalized supplier IDs from `products` for array-contains queries.
  supplier_ids: z.array(z.string()).optional(),
  // Set by the backend starter-content seeder to the source template concept id
  // when it clones a template at signup. Marks the concept as seeded (not
  // user-created) so cards can show a "Sample" badge. Absent on user concepts.
  seededFromTemplate: z.string().optional(),
  created_at: z.unknown(), // Firestore Timestamp
  updated_at: z.unknown(), // Firestore Timestamp
});
export type Concept = z.infer<typeof ConceptSchema>;

export const ConceptInputSchema = ConceptSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export type ConceptInput = z.infer<typeof ConceptInputSchema>;

// ============================================
// Concept Variants (Remix Architecture — issue #3303)
// ============================================
//
// Draft "candidate" renders produced by /generate-images and /remix. They live
// in the subcollection concepts/{conceptId}/variants/{variantId} and are
// **transient candidates** — NOT committed snapshots. They must never pollute
// concepts/{id}/versions or concepts/{id}/current.
//
// Variants are write-once EXCEPT for the allowed transitions handled by the
// backend helpers in conceptVariants.ts:
//   - state: PENDING → COMPLETED | FAILED
//   - img_url: null → <gcs url>   (set when the render completes)
//   - finalized_concept_id: null → <conceptId>  (backref set by /finalizeConcept)
// Nothing else about a variant doc is ever mutated.

export const VariantStateEnum = z.enum(["PENDING", "COMPLETED", "FAILED"]);
export type VariantState = z.infer<typeof VariantStateEnum>;

// Remix archetypes. The "kind" of transformation a remix batch applies to a
// source image. Null for the initial /generate-images batch.
export const RemixTypeEnum = z.enum([
  "STYLE",
  "MATERIALS",
  "LAYOUT",
  "FURNITURE",
  "COLORWAY",
  "CAMERA",
]);
export type RemixType = z.infer<typeof RemixTypeEnum>;

// Selectable remix directions per archetype. Single shared source of truth for
// the VariantGrid chips (frontend) and the prompt builder (backend) so the two
// never drift. Selected values are injected verbatim into the matching
// {selected_*} template variable. Values are placeholders, refine over time.
export const REMIX_DIRECTIONS: Record<RemixType, string[]> = {
  STYLE: [
    "Japandi",
    "Mid-Century",
    "Mediterranean",
    "California Organic",
    "Contemporary Luxe",
    "Parisian Editorial",
    "English Country",
    "Curated Eclectic",
  ],
  MATERIALS: [
    "Natural Stone",
    "Warm Wood",
    "Tiles",
    "Terracotta",
    "Concrete",
    "Matte Black",
    "Marble",
    "Plaster",
  ],
  LAYOUT: [
    "Open & Airy",
    "Zoned",
    "Symmetrical",
    "Dynamic",
    "Compact & Efficient",
  ],
  FURNITURE: [
    "Minimal",
    "Sculptural",
    "Vintage Mix",
    "Organic",
    "Contemporary Luxe",
    "Artisan Craft",
    "Retro",
    "Classic",
  ],
  COLORWAY: [
    "Earth Tones",
    "Monochrome",
    "Jewel Tones",
    "Pastels",
    "Black & White",
    "Warm Neutrals",
  ],
  CAMERA: [
    "Wide Angle",
    "Low Angle",
    "Close-Up",
    "Corner View",
    "Through Doorway",
    "Elevated View",
  ],
};

export const ConceptVariantSchema = z.object({
  id: z.string(),
  owner_id: z.string(), // user id of the concept owner
  concept_id: z.string(), // parent concept (always set)
  batch_id: z.string(), // groups one /generate-images or /remix call
  state: VariantStateEnum, // PENDING → COMPLETED | FAILED
  img_url: z.string().url().nullable(), // null while PENDING / on FAILED
  model: z.string(), // e.g. "gpt-image-2"
  aspect_ratio: z.string(), // requested ratio, e.g. "3:2"
  remix_type: RemixTypeEnum.nullable().optional(), // null for initial generation
  prompt: z.string().nullable().optional(), // prompt used to render this variant
  selected_options: z.array(z.string()).optional(), // chips picked by the user (e.g. "Terracotta")
  auto_direction: z.string().nullable().optional(),
  user_text: z.string().nullable().optional(), // free-text request (also baked into prompt)
  user_colors: z.array(z.string()).optional(), // picked colors e.g. "Terracotta (#C96C4A)"
  user_image_urls: z.array(z.string()).optional(), // reference image URLs used for this render
  source_variant_id: z.string().nullable().optional(), // lineage for remixes
  finalized_concept_id: z.string().nullable().optional(), // backref set on finalize
  render_ms: z.number().optional(),
  error_message: z.string().nullable().optional(),
  created_at: z.unknown(), // Firestore Timestamp
  updated_at: z.unknown(), // Firestore Timestamp
});
export type ConceptVariant = z.infer<typeof ConceptVariantSchema>;

export const ConceptVariantInputSchema = ConceptVariantSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export type ConceptVariantInput = z.infer<typeof ConceptVariantInputSchema>;

// ============================================
// Add Message Input/Response Types (for Cloud Function)
// ============================================

export const AddMessageInputMessageSchema = z.object({
  role: MessageRoleEnum,
  content: MessageContentSchema,
  state: MessageStateEnum,
  error_code: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  tool_call_id: z.string().optional(),
  structured_output: z.record(z.unknown()).optional(),
  edit_meta: EditMetaSchema.optional(), // Analytics summary on edit-box user messages (deploy-forward)
});

export const AddMessageConceptVersionSchema = z.object({
  img_url: z.string().url().nullable(),
  action_type: ActionTypeEnum,
  name: z.string().max(200).optional(),
  segments_url: z.string().url().nullable().optional(),
  narrative: z.string().optional(),
  location: z.string().optional(),
  budget: z.string().optional(),
  space: z.string().optional(),
  tag: z.string().optional(),
  style: z.string().optional(),
  palette: ConceptPaletteSchema.optional(),
  collection_id: z.string().nullable().optional(),
  // Project (projectsScheme) this concept belongs to (#3544 Phase 4).
  // Part of the immutable metadata-snapshot set: PRESERVED_VERSION_FIELDS
  // derives from ConceptVersionSchema, and client copyVersionMetadata copies it.
  project_id: z.string().nullable().optional(),
  region: z
    .union([z.array(RegionServedEnum), z.literal("GLOBAL")])
    .nullable()
    .optional(),
  parent_version_id: z.string().nullable().optional(),
  charge_credits: z.boolean().optional(),
});

export const AddMessageInputSchema = z.object({
  concept_id: z.string().optional(),
  message: AddMessageInputMessageSchema,
  concept_version: AddMessageConceptVersionSchema.optional(),
  source: z.string().optional(),
  // Concept-level, honoured only when this call creates the concept.
  origin: ConceptOriginEnum.optional(),
  source_model_id: SourceModelIdSchema.optional(),
});
export type AddMessageInput = z.infer<typeof AddMessageInputSchema>;

export const AddMessageResponseSchema = z.object({
  success: z.boolean(),
  message_id: z.string(),
  concept_version_id: z.string().nullable().optional(),
  thread_id: z.string(),
  concept_id: z.string(),
  is_new_concept: z.boolean(),
});
export type AddMessageResponse = z.infer<typeof AddMessageResponseSchema>;

// ============================================
// Reference Image Tags (shared between Creator UI + design_stream prompts)
// ============================================

/**
 * Vocabulary for reference-image tags selected by users in the Creator UI
 * (see apps/creator/src/design_stream/components/ImageUpload/ImageUpload.tsx).
 * Kept as a loose string union on the wire so older clients / future tags
 * don't break Zod validation.
 */
export const REFERENCE_IMAGE_TAGS = [
  "Inspiration",
  "Existing Space",
  "Floor Plan / Layout",
  "Material Reference",
  "Product / Furniture",
] as const;
export type ReferenceImageTag = (typeof REFERENCE_IMAGE_TAGS)[number];

// ============================================
// Artifact Schemas
// ============================================

export const ArtifactTypeEnum = z.enum(["CLOSEUP", "VIDEO", "UPSCALE"]);
export type ArtifactType = z.infer<typeof ArtifactTypeEnum>;

export const ArtifactSchema = z.object({
  id: z.string(),
  concept_id: z.string(),
  source_version_id: z.string(),
  source_message_id: z.string(),
  artifact_type: ArtifactTypeEnum,
  output_url: z.string().url(),
  thumbnail_url: z.string().url().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
  credits_cost: z.number(),
  created_at: z.unknown(), // Firestore Timestamp
});
export type Artifact = z.infer<typeof ArtifactSchema>;

export const AddMessageArtifactInputSchema = z.object({
  concept_id: z.string(),
  source_version_id: z.string(),
  artifact_type: ArtifactTypeEnum,
  message: z.object({
    content: MessageContentSchema,
  }),
  metadata: z.record(z.unknown()).optional(),
  // Workspace context for credit deduction. Set to a design_firms doc id
  // when the user is acting in a team workspace; null/omitted = personal.
  // (Phase A.2a.1.)
  design_firm_id: z.string().nullable().optional(),
});
export type AddMessageArtifactInput = z.infer<
  typeof AddMessageArtifactInputSchema
>;

// ============================================
// Artifact Constants (shared between frontend and backend)
// ============================================

/**
 * Worst-case timeout for the design-stream chat graph (LangGraph run including
 * Gemini/OpenAI image generation). Shared by the Express backend (graph.invoke
 * timeout) and the frontend (stuck-PENDING / "process not completed" detection)
 * so they can never disagree.
 */
export const CHAT_GRAPH_TIMEOUT_MS = 7 * 60 * 1000;

/** Credit cost per artifact type. Single source of truth for both client and server. */
export const ARTIFACT_CREDIT_COSTS: Record<ArtifactType, number> = {
  CLOSEUP: 1,
  VIDEO: 5,
  UPSCALE: 1,
};

/**
 * Max age (ms) before a PENDING artifact message is considered stale and retryable.
 *
 * Per-artifact-type so server aligns with the client-side staleness used in
 * MaterialCloseups (`CLOSEUP_STALE_MS`) and useCreateVideo (`VIDEO_STALE_MS`).
 * Keep these in sync with the client constants.
 */
export const STALE_PENDING_MS_BY_TYPE: Record<ArtifactType, number> = {
  CLOSEUP: 6 * 60 * 1000, // 6 min — keep in sync with CLOSEUP_STALE_MS
  VIDEO: 10 * 60 * 1000, // 10 minutes — VEO can take up to 8 minutes
  UPSCALE: 4 * 60 * 1000, // 4 minutes (upscale is fast)
};

// ============================================
// Relationships (for reference)
// ============================================
// concepts.current_version_id -> concept_versions.id
// concept_versions.concept_id -> concepts.id
// concept_versions.source_message_id -> messages.id
// messages.thread_id -> threads.id
// artifacts.source_version_id -> concept_versions.id
// artifacts.source_message_id -> messages.id

// Virtual Showrooms: the `showrooms/{supplierId}` layout document. Shared by
// the creator read path, the seed script and the (future) admin editor.
export {
  ShowroomSchema,
  ShowroomSectionSchema,
  ShowroomCategoryEnum,
  OrderSampleLinkSchema,
  SHOWROOM_CATEGORY_LABELS,
  SHOWROOM_SCHEMA_VERSION,
  collectShowroomProductIds,
  collectShowroomCollectionIds,
  collectShowroomBoardIds,
  type Showroom,
  type ShowroomSection,
  type ShowroomSectionType,
  type ShowroomCategory,
  type OrderSampleLink,
  HexColor,
  SHOWROOM_LIMITS,
  SHOWROOM_PREVIEW_READY,
  SHOWROOM_PREVIEW_DOC,
  SHOWROOM_PREVIEWS_SUBCOLLECTION,
  showroomPreviewPath,
  showroomSharePath,
} from "./showroom";

// Board composition: pure-logic helpers for picking products from a
// segments_url JSON. Shared between the AI backend (composeBoardAssets node)
// and the creator frontend (AutoComposeFromCollection autoBoardCompose path).
export {
  ACCESSORY_APPLICATION_TYPES,
  MATERIALS_PER_SEGMENT,
  MAX_ACCESSORIES,
  extractProductIdsForBoard,
  boardProductIdsFromSegments,
  type BoardProductSelection,
} from "./boardComposition";

// Web clipper productType → categories, shared with the creator.
export { CLIPPER_PRODUCT_TYPE_CATEGORIES } from "./clipperCategories";

// Teams sharing: the one permission function (UI, backend) and the derived
// readers/editors arrays the Firestore rules read.
export * from "./access";
